import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Flex } from "antd";
import { formatCount } from "../../../utils/formatNumber";
import { formatRate } from "../../../VAMP/utils/formatNumber";

const MCChartTooltip = forwardRef(function MCChartTooltip({ dataType }, ref) {
  const [payload, setPayload] = useState(null);

  useImperativeHandle(
    ref,
    () => ({
      update: (p) => {
        setPayload(p);
      },
    }),
    []
  );

  return createPortal(
    <TooltipContent {...payload} dataType={dataType} />,
    document.body
  );
});

const TooltipContent = ({ x, y, date, visible = false, data, dataType }) => {
  const tooltipRef = useRef(null);
  const { adjustedX, adjustedY } = useMemo(() => {
    const tooltipEl = tooltipRef.current;
    const rect = tooltipEl?.getBoundingClientRect();
    const width = rect?.width || 0;
    let adjustedX = x;
    const xTranslate = -(width / 2);
    const xTranslated = x + xTranslate;
    if (xTranslated + width > window.innerWidth) {
      adjustedX = window.innerWidth + xTranslate;
    }
    if (xTranslated < 0) {
      adjustedX = -xTranslate;
    }
    return { adjustedX, adjustedY: y };
  }, [x, y]);

  const groups = useMemo(() => {
    if (!Array.isArray(data)) return { thresholds: [], series: [] };
    const hasValue = (p) => p?.value != null && !Number.isNaN(p.value);
    const thresholds = [];
    const map = new Map();
    data.forEach((point) => {
      const type = point?.datasetType;
      if (!type) return;
      if (type.endsWith("_zone")) {
        thresholds.push(point);
        return;
      }
      if (!hasValue(point)) return;
      const isPrev = type.endsWith("_previous");
      const key = isPrev ? type.slice(0, -"_previous".length) : type;
      if (!map.has(key)) map.set(key, { key, main: null, prev: null });
      const entry = map.get(key);
      if (isPrev) entry.prev = point;
      else entry.main = point;
    });
    return { thresholds, series: Array.from(map.values()) };
  }, [data]);

  const hasAnyRow =
    groups.thresholds.length > 0 ||
    groups.series.some((g) => g.main || g.prev);

  const style = {
    top: adjustedY,
    left: adjustedX,
    opacity: visible && hasAnyRow ? 1 : 0,
  };

  const formatValue = (v) =>
    dataType === "rate" ? formatRate(v) : formatCount(v);

  return (
    <div id="health-mc-chart-tooltip" style={style} ref={tooltipRef}>
      <Flex vertical gap={8}>
        {groups.thresholds.length > 0 && (
          <Flex vertical gap={2} className="tooltip-group">
            {groups.thresholds.map((point) => (
              <Row
                key={point.datasetType}
                point={point}
                label={point.label}
                format={formatValue}
              />
            ))}
          </Flex>
        )}
        {groups.series.some((g) => g.main) && (
          <Flex vertical gap={2} className="tooltip-group">
            <span className="tooltip-group-title">{date}</span>
            {groups.series.map(
              (group) =>
                group.main && (
                  <Row
                    key={group.key}
                    point={group.main}
                    label={group.main.label}
                    format={formatValue}
                  />
                )
            )}
          </Flex>
        )}
        {groups.series.some((g) => g.prev) && (
          <Flex vertical gap={2} className="tooltip-group">
            <span className="tooltip-group-title">
              {groups.series.find((g) => g.prev)?.prev?.comparisonDate}
            </span>
            {groups.series.map(
              (group) =>
                group.prev && (
                  <Row
                    key={group.key}
                    point={group.prev}
                    label={group.main?.label || group.prev.label}
                    format={formatValue}
                  />
                )
            )}
          </Flex>
        )}
      </Flex>
    </div>
  );
};

const Row = ({ point, label, format }) => (
  <Flex justify="space-between" align="center" gap={12} className="tooltip-row">
    <Flex gap={8} align="center">
      <span
        className={`line-dataset-indicator${point?.dashed ? " dashed" : ""}`}
        style={
          point?.dashed
            ? {
                backgroundImage: `repeating-linear-gradient(to right, ${point?.backgroundColor} 0 3px, transparent 3px 6px)`,
              }
            : { background: point?.backgroundColor }
        }
      />
      <span>{label}</span>
    </Flex>
    <span className="tooltip-value">{format(point?.value)}</span>
  </Flex>
);

export default MCChartTooltip;
